import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../App.jsx';
import api from '../services/api.js';
import NavBar from '../components/NavBar.jsx';
import QuestionImage from '../components/QuestionImage.jsx';
import OnboardingModal from '../components/OnboardingModal.jsx';

const DURATION = 60;

export default function Blitz() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [phase, setPhase] = useState('lobby');
  const [sessionId, setSessionId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(DURATION);
  const [answering, setAnswering] = useState(false);
  const [lastResult, setLastResult] = useState(null); // {correct, correctId}
  const [ranking, setRanking] = useState([]);
  const [showBlitzTip, setShowBlitzTip] = useState(false);
  const [mistakes, setMistakes] = useState([]); // [{question, correctText, selectedText, explanation}]
  const startTimeRef = useRef(null);
  const rafRef = useRef(null);
  const scoreRef = useRef(0);
  const sessionRef = useRef(null);
  const phaseRef = useRef('lobby');

  // Keep refs in sync
  useEffect(() => { scoreRef.current = score; }, [score]);
  useEffect(() => { sessionRef.current = sessionId; }, [sessionId]);
  useEffect(() => { phaseRef.current = phase; }, [phase]);

  useEffect(() => {
    api.getBlitzRanking().then(r => setRanking(r.ranking || [])).catch(() => {});
  }, []);

  // Popup d'intro : affichée une seule fois (flag local, comme un onboarding).
  useEffect(() => {
    if (!localStorage.getItem('seen_blitz_tip')) setShowBlitzTip(true);
  }, []);

  const dismissBlitzTip = () => {
    setShowBlitzTip(false);
    try { localStorage.setItem('seen_blitz_tip', '1'); } catch (e) { /* non bloquant */ }
  };

  const startBlitz = async () => {
    try {
      const res = await api.startBlitz();
      sessionRef.current = res.session_id;
      setSessionId(res.session_id);
      setQuestions(res.questions || []);
      setCurrentIdx(0);
      setScore(0);
      scoreRef.current = 0;
      setMistakes([]);
      setTimeLeft(DURATION);
      setAnswering(false);
      phaseRef.current = 'playing';
      setPhase('playing');
    } catch (e) { alert('Erreur: ' + e.message); }
  };

  // Timer
  useEffect(() => {
    if (phase !== 'playing') return;
    startTimeRef.current = Date.now();
    const tick = () => {
      if (phaseRef.current !== 'playing') return;
      const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
      const remaining = Math.max(0, DURATION - elapsed);
      setTimeLeft(remaining);
      if (remaining <= 0) {
        finishBlitz();
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [phase]);

  const finishBlitz = () => {
    if (phaseRef.current === 'finished') return;
    phaseRef.current = 'finished';
    setPhase('finished');
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    // Save score
    const sid = sessionRef.current;
    const sc = scoreRef.current;
    if (sid) {
      api.request('POST', `/quiz/blitz/${sid}/finish`, { score: sc }).catch(() => {});
    }
    api.getBlitzRanking().then(r => setRanking(r.ranking || [])).catch(() => {});
  };

  const answer = async (answerId) => {
    if (answering || phaseRef.current !== 'playing') return;
    const q = questions[currentIdx];
    if (!q) return;
    setAnswering(true);
    let res;
    try {
      res = await api.answerBlitz(sessionRef.current, q.id, answerId);
      setLastResult({ correct: res.correct, correctId: res.correct_answer_id, selectedId: answerId });
      if (res.correct) {
        const newScore = scoreRef.current + 1;
        scoreRef.current = newScore;
        setScore(newScore);
      } else {
        // Tracker l'erreur pour le récap
        const correctAns = q.answers.find(a => a.id === res.correct_answer_id);
        const selectedAns = q.answers.find(a => a.id === answerId);
        setMistakes(prev => [...prev, {
          question: q.text,
          image_url: q.image_url,
          correctText: correctAns ? correctAns.text : '?',
          selectedText: selectedAns ? selectedAns.text : '?',
          explanation: res.explanation || '',
        }]);
      }
    } catch (e) {
      console.error('Blitz answer error:', e);
    }
    // Next question after brief pause to show feedback
    setTimeout(() => {
      setAnswering(false);
      setLastResult(null);
      const nextIdx = currentIdx + 1;
      if (nextIdx < questions.length && phaseRef.current === 'playing') {
        setCurrentIdx(nextIdx);
      } else {
        finishBlitz();
      }
    }, 300);
  };

  const q = (phase === 'playing' && currentIdx < questions.length) ? questions[currentIdx] : null;
  const pct = (timeLeft / DURATION) * 100;
  const timerColor = timeLeft > 20 ? '#34e0c4' : timeLeft > 10 ? '#F5A623' : '#ff4444';

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <NavBar />

      {showBlitzTip && (
        <OnboardingModal
          emoji="⚡"
          title="Partie éclair"
          buttonLabel="C'est parti ! ⚡"
          onClose={dismissBlitzTip}
        >
          Le but : enchaîner <strong>un maximum de bonnes réponses en 60 secondes</strong>.
          <br /><br />
          Les questions sont <strong>tirées au hasard</strong>, <strong>parmi tes thèmes débloqués</strong> et <strong>tous niveaux de difficulté mélangés</strong> — ici pas de difficulté adaptative, c'est le <strong>sprint</strong> ! Fais le meilleur score.
        </OnboardingModal>
      )}

      <div className="container">
        <button className="back-btn" onClick={() => navigate('/')}>← Retour</button>

        {phase === 'lobby' && (
          <div style={{ textAlign: 'center', marginTop: 40 }}>
            <div style={{ fontSize: '3rem', marginBottom: 16 }}>⚡</div>
            <h2 style={{ color: '#F5A623', marginBottom: 8 }}>Partie éclair</h2>
            <p style={{ color: 'var(--text-dim)', marginBottom: 24, maxWidth: 400, margin: '0 auto 24px' }}>
              Réponds au maximum de questions en 60 secondes ! Tes thèmes débloqués sont mélangés.
            </p>
            <button className="btn btn-primary" onClick={startBlitz}
              style={{ fontSize: '1.1rem', padding: '14px 40px' }}>⚡ C'est parti !</button>
            {ranking.length > 0 && (
              <div style={{ marginTop: 40 }}>
                <h3 style={{ color: 'var(--text-dim)', marginBottom: 16 }}>🏅 Meilleurs scores</h3>
                {ranking.map((r, i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px',
                    background: i === 0 ? 'rgba(255,215,0,0.1)' : 'rgba(255,255,255,0.03)',
                    borderRadius: 10, marginBottom: 6, maxWidth: 350, margin: '0 auto 6px',
                    border: i === 0 ? '1px solid rgba(255,215,0,0.2)' : '1px solid transparent',
                  }}>
                    <span style={{ fontWeight: 800, color: i === 0 ? '#f2c063' : 'var(--text-muted)', width: 24 }}>
                      {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i+1}.`}
                    </span>
                    <span style={{ fontSize: '1.2rem' }}>{r.avatar}</span>
                    <span style={{ flex: 1, fontWeight: 600 }}>{r.name}</span>
                    <span style={{ fontWeight: 800, color: '#F5A623', fontSize: '1.1rem' }}>{r.best_score}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {phase === 'playing' && q && (
          <div style={{ maxWidth: 600, margin: '0 auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <div style={{ flex: 1, height: 8, borderRadius: 4, background: 'rgba(255,255,255,0.1)', overflow: 'hidden' }}>
                <div style={{ width: `${pct}%`, height: '100%', borderRadius: 4,
                  background: timerColor, transition: 'width 0.3s linear, background 0.5s' }} />
              </div>
              <div style={{ fontWeight: 800, fontSize: '1.2rem', color: timerColor, minWidth: 40, textAlign: 'right' }}>
                {timeLeft}s
              </div>
            </div>
            <div style={{ textAlign: 'center', marginBottom: 12 }}>
              <span style={{ fontSize: '2rem', fontWeight: 800, color: '#F5A623' }}>{score}</span>
              <span style={{ color: 'var(--text-muted)', marginLeft: 4 }}>pts</span>
            </div>
            <div style={{
              background: 'var(--bg-card)', borderRadius: 16, padding: 24,
              border: '1px solid rgba(52,224,196,0.1)',
            }}>
              {q.image_url && <QuestionImage imageUrl={q.image_url} />}
              <p style={{ fontWeight: 700, fontSize: '1rem', marginBottom: 16, marginTop: q.image_url ? 12 : 0 }}>{q.text}</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {q.answers.map(a => {
                  let bg = 'rgba(255,255,255,0.05)';
                  let border = 'rgba(255,255,255,0.1)';
                  if (lastResult) {
                    if (a.id === lastResult.correctId) { bg = 'rgba(52,224,196,0.25)'; border = '#34e0c4'; }
                    else if (a.id === lastResult.selectedId && !lastResult.correct) { bg = 'rgba(255,107,107,0.25)'; border = '#ff7a6b'; }
                  }
                  return (
                  <button key={a.id} onClick={() => answer(a.id)}
                    disabled={answering}
                    style={{
                      padding: '12px', borderRadius: 10, fontWeight: 600,
                      background: bg, border: `2px solid ${border}`,
                      color: 'var(--text)', cursor: answering ? 'default' : 'pointer',
                      fontSize: '0.85rem', textAlign: 'left', transition: 'all 0.15s',
                      touchAction: 'manipulation',
                    }}>
                    {a.text}
                  </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {phase === 'playing' && !q && (
          <div style={{ textAlign: 'center', marginTop: 40, color: 'var(--text-dim)' }}>
            Plus de questions disponibles ! Score final : {score}
          </div>
        )}

        {phase === 'finished' && (
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
            style={{ textAlign: 'center', marginTop: 40 }}>
            <div style={{ fontSize: '3rem', marginBottom: 16 }}>🏁</div>
            <h2 style={{ color: '#F5A623', marginBottom: 8 }}>Temps écoulé !</h2>
            <div style={{ fontSize: '3rem', fontWeight: 800, color: '#34e0c4', marginBottom: 8 }}>{score}</div>
            <p style={{ color: 'var(--text-dim)', marginBottom: 24 }}>
              bonne{score > 1 ? 's' : ''} réponse{score > 1 ? 's' : ''} en {DURATION}s
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button className="btn btn-primary" onClick={startBlitz}>⚡ Rejouer</button>
              <button className="btn" onClick={() => navigate('/')}>Retour</button>
            </div>

            {mistakes.length > 0 && (
              <div style={{ marginTop: 32, textAlign: 'left', maxWidth: 500, margin: '32px auto 0' }}>
                <h3 style={{ color: 'var(--text-dim)', marginBottom: 12, textAlign: 'center' }}>
                  📖 À revoir ({mistakes.length})
                </h3>
                {mistakes.map((m, i) => (
                  <div key={i} style={{
                    background: 'var(--bg-card)',
                    borderRadius: 12,
                    padding: 14,
                    marginBottom: 10,
                    border: '1px solid rgba(255, 107, 107, 0.2)',
                  }}>
                    <p style={{ fontWeight: 700, fontSize: '0.95rem', marginBottom: 10 }}>{m.question}</p>
                    <div style={{ fontSize: '0.85rem', marginBottom: 6 }}>
                      <span style={{ color: '#ff7a6b' }}>❌ Ta réponse : </span>
                      <span style={{ color: 'var(--text-dim)' }}>{m.selectedText}</span>
                    </div>
                    <div style={{ fontSize: '0.85rem', marginBottom: m.explanation ? 8 : 0 }}>
                      <span style={{ color: '#34e0c4' }}>✓ Bonne réponse : </span>
                      <span style={{ color: 'var(--text)', fontWeight: 600 }}>{m.correctText}</span>
                    </div>
                    {m.explanation && (
                      <p style={{ fontSize: '0.8rem', color: 'var(--text-dim)', fontStyle: 'italic', marginTop: 8, paddingTop: 8, borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                        💡 {m.explanation}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
            {ranking.length > 0 && (
              <div style={{ marginTop: 32 }}>
                <h3 style={{ color: 'var(--text-dim)', marginBottom: 16 }}>🏅 Classement éclair</h3>
                {ranking.map((r, i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px',
                    background: i === 0 ? 'rgba(255,215,0,0.1)' : 'rgba(255,255,255,0.03)',
                    borderRadius: 10, marginBottom: 6, maxWidth: 350, margin: '0 auto 6px',
                    border: i === 0 ? '1px solid rgba(255,215,0,0.2)' : '1px solid transparent',
                  }}>
                    <span style={{ fontWeight: 800, color: i === 0 ? '#f2c063' : 'var(--text-muted)', width: 24 }}>
                      {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i+1}.`}
                    </span>
                    <span style={{ fontSize: '1.2rem' }}>{r.avatar}</span>
                    <span style={{ flex: 1, fontWeight: 600 }}>{r.name}</span>
                    <span style={{ fontWeight: 800, color: '#F5A623', fontSize: '1.1rem' }}>{r.best_score}</span>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}
